import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, RefreshCw, AlertCircle, Users } from 'lucide-react';
import { colors } from '../../constants/theme';
import { competitionService } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';

export default function GroupEditor() {
  const [groupsByCategory, setGroupsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [swapping, setSwapping] = useState(false);
  const [selected, setSelected] = useState([]); // [{ playerId, name, category, groupName }]

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      setSelected([]);
      const resp = await competitionService.getGroups();
      if (resp.success) {
        setGroupsByCategory(resp.data.groups || {});
      } else {
        setError('Nenhuma competição ativa com grupos.');
      }
    } catch (e) {
      setError(e.error?.message || 'Erro ao carregar grupos. É preciso ter uma competição com grupos sorteados.');
    } finally {
      setLoading(false);
    }
  };

  const isSelected = (playerId) => selected.some((s) => s.playerId === playerId);

  const toggleSelect = (player, category, groupName) => {
    setError(null);
    if (isSelected(player.id)) {
      setSelected((prev) => prev.filter((s) => s.playerId !== player.id));
      return;
    }
    const entry = { playerId: player.id, name: player.name, category, groupName };
    if (selected.length === 0) {
      setSelected([entry]);
      return;
    }
    // Já há um selecionado: validar mesma categoria e grupo diferente
    const first = selected[0];
    if (first.category !== category) {
      setError('Só é possível trocar atletas da mesma categoria.');
      return;
    }
    if (first.groupName === groupName) {
      setError('Os dois atletas precisam estar em grupos diferentes.');
      return;
    }
    setSelected([first, entry]);
  };

  const handleSwap = async () => {
    if (selected.length !== 2) return;
    setSwapping(true);
    setError(null);
    try {
      const resp = await competitionService.swapPlayers(selected[0].playerId, selected[1].playerId);
      if (resp.success) {
        await loadGroups();
      }
    } catch (e) {
      setError(e.error?.message || 'Erro ao trocar atletas.');
    } finally {
      setSwapping(false);
    }
  };

  if (loading) return <LoadingSpinner size="medium" text="Carregando grupos..." />;

  const categories = Object.keys(groupsByCategory);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
          Editar Grupos
        </h2>
        <button
          onClick={loadGroups}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-2">
        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p>
            Selecione <strong>um atleta de cada grupo</strong> (mesma categoria) e clique em
            "Trocar selecionados". Os dois trocam de grupo e as partidas desses grupos são refeitas.
          </p>
          <p className="mt-1">
            Só é possível trocar enquanto <strong>nenhuma partida</strong> dos grupos envolvidos tiver começado.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {selected.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sticky top-2 z-10 border-2" style={{ borderColor: colors.primary }}>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium">Selecionados:</span>
            {selected.map((s) => (
              <span key={s.playerId} className="px-2 py-1 bg-gray-100 rounded">
                {s.name} <span className="text-gray-400">({s.groupName})</span>
              </span>
            ))}
          </div>
          <button
            onClick={handleSwap}
            disabled={selected.length !== 2 || swapping}
            className="flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: colors.primary }}
          >
            <ArrowLeftRight className="h-4 w-4" />
            {swapping ? 'Trocando...' : 'Trocar selecionados'}
          </button>
        </div>
      )}

      {categories.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">Nenhum grupo encontrado. Sorteie os grupos primeiro.</p>
        </div>
      ) : (
        categories.map((category) => {
          const groups = groupsByCategory[category];
          const groupNames = Object.keys(groups);
          return (
            <div key={category} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-3" style={{ backgroundColor: colors.primary }}>
                <h3 className="text-white font-semibold capitalize">Categoria {category}</h3>
              </div>
              <div className="p-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {groupNames.map((groupName) => {
                  const players = Array.isArray(groups[groupName]) ? groups[groupName] : [];
                  return (
                    <div key={groupName} className="border rounded-lg p-3">
                      <h4 className="font-medium mb-2 capitalize" style={{ color: colors.primary }}>
                        {groupName}
                      </h4>
                      <ul className="space-y-1">
                        {players.map((p) => {
                          const sel = isSelected(p.id);
                          return (
                            <li key={p.id}>
                              <button
                                onClick={() => toggleSelect(p, category, groupName)}
                                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                                  sel
                                    ? 'text-white'
                                    : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                                }`}
                                style={sel ? { backgroundColor: colors.primary } : {}}
                              >
                                {p.name}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
